import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import { IconClose } from './Icons';
import VodafoneBadge from './VodafoneBadge';
import './phases.css';

const nf = (n) => (n || 0).toLocaleString('it-IT');

/* Dialog dell'agente VF (Vodafone): animazione + statistiche + velocità.
   Gemello di SsmateStatsDialog, con i dati di /gea/stato. */
export default function VfStatsDialog({ open, onClose }) {
  const [info, setInfo] = useState(null);
  const [closing, setClosing] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [speed, setSpeed] = useState(null);     // numeri/min (calcolata lato client)
  const sample = useRef(null);
  const [sondando, setSondando] = useState(false);

  const handleClose = () => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => { setClosing(false); onClose?.(); }, 320);
  };

  useEffect(() => {
    if (!open) return;
    setClosing(false); setConfirmStop(false); setSpeed(null); sample.current = null;
    const load = () => axios.get(`${API}/gea/stato`).then((r) => {
      setInfo(r.data);
      const j = r.data?.job;
      if (j && j.status === 'running') {
        const now = Date.now();
        const prev = sample.current;
        if (prev && j.completed > prev.c) {
          const dm = (now - prev.t) / 60000;
          if (dm > 0) setSpeed(Math.round((j.completed - prev.c) / dm));
        }
        sample.current = { c: j.completed, t: now };
      } else { sample.current = null; setSpeed(null); }
    }).catch(() => {});
    load();
    const id = setInterval(load, 1500);
    return () => clearInterval(id);
  }, [open]);

  const ferma = async () => {
    setStopping(true);
    try { await axios.post(`${API}/gea/agente`, { attivo: false }); toast.info('AI VF fermato'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setStopping(false); setConfirmStop(false);
  };

  const provaSonda = async () => {
    setSondando(true);
    try {
      const r = await axios.post(`${API}/gea/sonda`);
      if (r.data?.stato === 'ok') toast.success(`Il portale risponde: ${r.data.numero} risulta Vodafone`);
      else if (r.data?.stato === 'bloccato') toast.error(`Vodafone ci sta bloccando: ${r.data.numero} risulta «${r.data.risultato}»`);
      else toast.info(r.data?.motivo || 'Prova non conclusiva');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setSondando(false);
  };

  if (!open) return null;

  const job = info?.job;
  const running = job && job.status === 'running';
  const inAttesa = info?.in_attesa;
  const pct = job && job.total ? Math.round((100 * job.completed) / job.total) : 0;

  return createPortal(
    <div className={`bulk-overlay${closing ? ' closing' : ''}`}>
      <div className="bulk-dialog">
        <div className="bulk-bg">
          <div className="bulk-grid" />
          {running && (
            <div className="bulk-radar">
              <span className="bulk-ring r0" /><span className="bulk-ring r1" />
              <span className="bulk-ring r2" /><span className="bulk-ring r3" />
            </div>
          )}
        </div>

        <div className="bulk-content">
          <div className="bulk-head">
            <span className="bulk-title">AI VF · Vodafone</span>
            <button className="bulk-x" onClick={handleClose}><IconClose size={16} color="#fff" /></button>
          </div>

          {info?.blocco?.bloccato && (
            <div className="vf-blocco">
              <span className="vf-blocco-ico">!</span>
              <div>
                <b>Vodafone ci sta bloccando.</b>
                <div>{info.blocco.motivo}</div>
                <div className="vf-blocco-da">dalle {new Date(info.blocco.da).toLocaleString('it-IT')}</div>
                <div>L’agente si è fermato da solo. Conviene aspettare qualche ora, poi riprovare.</div>
              </div>
            </div>
          )}

          {!running ? (
            <div className="bulk-empty">
              <div className="bulk-empty-ico"><VodafoneBadge size={30} style={{ filter: 'grayscale(1)', opacity: .6 }} /></div>
              <p>{inAttesa ? 'AI VF in attesa di record' : 'AI VF fermo'}</p>
              {info?.eligibili != null && (
                <p style={{ fontSize: 12, opacity: .7 }}>Verificabili: <b>{nf(info.eligibili)}</b></p>
              )}
              {info?.vodafone != null && (
                <p style={{ fontSize: 11, opacity: .6 }}>Vodafone trovati (totale): <b>{nf(info.vodafone)}</b></p>
              )}
            </div>
          ) : (
            <div className="ph-wrap">
              <div className="ph-label">Verifica Vodafone</div>
              <div className="ph-sub">
                {job.current ? <>verifico: <span className="ph-num">{job.current}</span></> : 'preparazione…'}
              </div>

              <div className="ph-stats">
                <div className="ph-stats-head">
                  <span>{nf(job.completed)}/{nf(job.total)} ({pct}%)</span>
                  {speed != null && <span style={{ opacity: .85 }}>{nf(speed)} num/min</span>}
                </div>
                <div className="ph-stack">
                  <span style={{ width: `${pct}%`, background: '#e60000' }} />
                </div>
                <div className="ph-legend" style={{ flexWrap: 'wrap' }}>
                  <span><i style={{ background: '#e60000' }} />Vodafone {nf(job.vodafone)}</span>
                  <span><i style={{ background: '#22c55e' }} />Nuovi {nf(job.nuovi)}</span>
                  <span><i style={{ background: '#94a3b8' }} />Negativi {nf(job.negativi)}</span>
                  <span><i style={{ background: '#f59e0b' }} />Disservizi {nf(job.disservizi)}</span>
                  <span><i style={{ background: '#ef4444' }} />Errori {nf(job.errori)}</span>
                </div>
              </div>

              {confirmStop ? (
                <div className="bulk-confirm">
                  <span className="bulk-confirm-q">Fermare l'agente VF?</span>
                  <div className="bulk-confirm-btns">
                    <button className="bulk-cancel" onClick={ferma} disabled={stopping}>{stopping ? '…' : 'Sì'}</button>
                    <button className="bulk-keep" onClick={() => setConfirmStop(false)} disabled={stopping}>No</button>
                  </div>
                </div>
              ) : (
                <button className="bulk-cancel" onClick={() => setConfirmStop(true)}>Ferma agente</button>
              )}
            </div>
          )}
          {info?.ritmo && (
            <div className="vf-ritmo">
              <div className="vf-ritmo-head">
                <span>Ritmo</span>
                <span className="vf-oggi">oggi {nf(info.oggi)} di {nf(info.ritmo.tetto)}</span>
              </div>
              <div className="vf-ritmo-piede">
                <span style={{ fontSize: 11.5, opacity: .75 }}>
                  pausa {info.ritmo.pausa}s · i parametri si cambiano dalla gestione agenti
                </span>
                <button type="button" className="bulk-keep" onClick={provaSonda} disabled={sondando}
                        title="Verifica una linea che sappiamo essere Vodafone: dice se il portale ci risponde ancora davvero">
                  {sondando ? 'Provo…' : 'Riprova adesso'}
                </button>
                {info?.sonda?.stato && (
                  <span className={`vf-sonda ${info.sonda.stato}`}>
                    sonda {info.sonda.stato === 'ok' ? 'verde' : info.sonda.stato === 'bloccato' ? 'rossa' : 'incerta'}
                    {info.sonda.at ? ` · ${new Date(info.sonda.at).toLocaleDateString('it-IT')}` : ''}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
